import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";

import PropTypes from "prop-types";

import BasePadding from "@cayuse/shared-react.components.base-padding";
import PaddedCell from "@cayuse/shared-react.components.padded-cell";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import AssignSelected from "components/AssignSelected";
import DownloadCSVModal from "components/DownloadCSVModal";
import FacetedSearchBar from "components/FacetedSearchBar";
import FeedTable from "components/FeedTable";
import SetView from "components/SetView";
import SPLink from "components/SPLink";
import {
	PROPOSALS_BASE_ROUTE,
	PRIVILEGES,
	ROLE_SP_PROPOSAL_ADMINISTRATOR,
	COLUMN_HEADER_KEYS,
} from "globals/constants";
import {
	useDictionary,
	usePagination,
	useSelectedRows,
	useHasPrivileges,
	useHasRoles,
	useDownloadCsv,
	useColumnHeaders,
	useRequestChain,
	useActiveColumns
} from 'hooks';
import {
	findProposals,
	setPaging,
	updateAdminTeamMembersPerProposal,
} from "store/actions/proposals";
import {
	getKeysFromColumnHeaders,
	generateDatedDownloadFileName,
	exportReportAsCSV,
	notifyError
} from "utils";
import { proposalListSelectors, shellSelectors } from "store/selectors";

import styles from "./styles.css";
import { isEqual } from 'lodash';

const ProposalListView = (
	{ proposalListId,
		searchFacets,
		onSearchChange,
		availableColumns,
		actualFacets
	}) => {
	const { pageLimit, pageOffset, setPageLimit, setPageNumber } = usePagination("proposals", setPaging);
	const [ isAssignmentOpen, setIsAssignmentOpen ] = useState(false);
	const [ isSetViewOpen, setIsSetViewOpen ] = useState(false);
	const [ isDownloadOpen, setIsDownloadOpen ] = useState(false);
	const [ isAssignToMeOnly, setIsAssignToMeOnly ] = useState(false);
	const [ rows, setRows ] = useState([]);
	const getRowKey = useMemo(() => row => row._item.id, []);
	const { selectedRows, isSelectAll, setRowSelection, clearSelections } = useSelectedRows({ rows, getRowKey });
	const [ previousSearch, setPreviousSearch ] = useState({});

	const facetFilterRequestChain = useRequestChain();

	const dictionary = useDictionary("proposals");

	const { isDownloading, download } = useDownloadCsv();

	const {
		[PRIVILEGES.PROPOSAL_ADMIN_TEAM_EDIT]: canAssign
	} = useHasPrivileges([
		PRIVILEGES.PROPOSAL_ADMIN_TEAM_EDIT
	]);

	const {
		[ROLE_SP_PROPOSAL_ADMINISTRATOR]: isProposalAdmin
	} = useHasRoles([
		ROLE_SP_PROPOSAL_ADMINISTRATOR
	]);

	const {
		items,
		totalItems,
		search,
		isFetching: isLoading,
		isRefresh,
	} = useSelector(proposalListSelectors.content);
	const user = useSelector(shellSelectors.user);
	const { columnHeaders: formColumnHeaders } = useColumnHeaders(COLUMN_HEADER_KEYS.PROPOSAL);
	const { columns: activeColumns, updateActiveColumns } = useActiveColumns("PROPOSAL", formColumnHeaders);

	const updatedActiveColumns = useMemo(
		() => activeColumns.map(activeColumn => formColumnHeaders.find(formColumnHeader => {
			if (isNonEmptyString(formColumnHeader.specialKey)) {
				formColumnHeader.keys = [ ...formColumnHeader.keys, formColumnHeader.specialKey ];
			}

			return activeColumn.id === formColumnHeader.id;
		})),
		[ activeColumns, formColumnHeaders ],
	);

	const requiredFieldKeys = useMemo(() => getKeysFromColumnHeaders(activeColumns), [ activeColumns ]);

	const onAssignedToMeClicked = () => {
		setIsAssignToMeOnly(!isAssignToMeOnly);
		setPageNumber(1);
	};

	const showAssignedToMeButton = isProposalAdmin
		? (
			<label className={styles.assignedToMeOnly}>
				{dictionary.assignedToMeOnly || "See assigned to me only"}
				<input
					name="assignButton"
					id="assignButton"
					type="checkbox"
					aria-label={dictionary.assignedToMeOnly || "See assigned to me only"}
					onClick={onAssignedToMeClicked}
				/>
			</label>
		) : "";

	const handleTeamAssignment = idTeamArray => {
		clearSelections();
		updateAdminTeamMembersPerProposal(idTeamArray);
	};

	const assignSelectedContent = canAssign
		? (
			<AssignSelected
				editMembers={false}
				instanceFeedItemSelections={selectedRows}
				isOpen={isAssignmentOpen}
				onTeamModification={handleTeamAssignment}
				onOpen={() => setIsAssignmentOpen(true)}
				onClose={() => setIsAssignmentOpen(false)}
				adminRole={ROLE_SP_PROPOSAL_ADMINISTRATOR}
				formType={COLUMN_HEADER_KEYS.PROPOSAL}
				isFeed
			/>
		)
		: "";


	const downloadCSV = async ({ selections: selectedColumnHeaders }) => {
		const filename = generateDatedDownloadFileName({
			prefix: dictionary.filePrefix || "SP_Proposals",
			extension: "csv"
		});

		await exportReportAsCSV({
			submissionType: "proposal",
			selectedColumnHeaders,
			filename,
			download,
			onComplete: () => setIsDownloadOpen(false),
			onError: notifyError
		});
	};

	const hasItems = isNonEmptyArray(items);

	useEffect(() => {
		if (!isEqual(search, previousSearch)) {
			setPreviousSearch(search);
			setPageNumber(1);
		}
	}, [ previousSearch, search ]);

	useEffect(() => {
		const updatedRows = hasItems
			? items.map(item => {
				const isNumberLinkable = isNonEmptyString(item.proposalId) && isNonEmptyString(item.proposalNumber);
				const linkedProposalNumber = isNumberLinkable
					? <SPLink to={`${PROPOSALS_BASE_ROUTE}/${item.proposalId}`}>{item.proposalNumber}</SPLink>
					: "";

				return {
					_isSelected: selectedRows.has(item.id),
					_item: item,
					...item,
					proposalNumber: linkedProposalNumber
				};
			})
			: [];

		setRows(updatedRows);
	}, [ items, selectedRows, hasItems, activeColumns ]);

	const combinedSearchTerms = useMemo(() => {
		const assignToMeTerm = isAssignToMeOnly
			? { "adminTeamMembers.personId": user?.personId || "" }
			: {};

		return { ...search, ...assignToMeTerm };
	}, [ search, isAssignToMeOnly, user ]);

	useEffect(() => {
		if (activeColumns?.length > 0 && (!isEqual(previousSearch, search) || !isLoading)) {
			facetFilterRequestChain.addRequest(
				'refreshProposals',
				() => {
					updateActiveColumns(updatedActiveColumns, false);

					return findProposals({
						pageLimit,
						pageOffset,
						searchTerms: combinedSearchTerms,
						fieldKeys: requiredFieldKeys
					});
				});
		}
	}, [
		combinedSearchTerms,
		pageLimit,
		pageOffset,
		requiredFieldKeys,
		updatedActiveColumns,
	]);

	return (
		<div id="list-grid">
			<VerticalSpacing>
				<BasePadding>
					<PaddedCell className={styles.proposalListOptions}
						radius={{ top: true }}
						border={{ top: true, left: true, right: true }}
					>
						<div className={styles.proposalListSearch}>
							<FacetedSearchBar
								ariaLabel={dictionary.proposalSearchAriaLabel || "Search proposals"}
								facetAriaControls={proposalListId}
								facets={searchFacets}
								onChange={onSearchChange}
								actualFacets={actualFacets}
								type="PROPOSAL"
							/>
						</div>
						<div className={styles.proposalListActions}>
							{showAssignedToMeButton}

							{assignSelectedContent}

							<SetView
								isOpen={isSetViewOpen}
								options={availableColumns}
								selections={activeColumns}
								onOpen={() => setIsSetViewOpen(true)}
								onClose={() => setIsSetViewOpen(false)}
								onUpdate={updateActiveColumns}
								requireKeys={false}
							/>

							<DownloadCSVModal
								isOpen={isDownloadOpen}
								onClick={() => setIsDownloadOpen(!isDownloadOpen)}
								onCancel={() => setIsDownloadOpen(false)}
								onSave={downloadCSV}
								options={availableColumns}
								selections={activeColumns}
								isDownloading={isDownloading}
							/>
						</div>
					</PaddedCell>

					<div>
						<FeedTable isLoading={isLoading}
							tableid={proposalListId}
							isRefresh={isRefresh}
							headers={activeColumns}
							rows={rows}
							totalItems={totalItems}
							pageLimit={pageLimit}
							pageOffset={pageOffset}
							onPageChange={setPageNumber}
							onPageLimitChange={setPageLimit}
							onRowSelectionChange={({ row, isSelected }) => setRowSelection(row, isSelected)}
							overflow="visible"
							isSelectable={canAssign}
							isSelectAll={isSelectAll}
							accessibilityLabel={({ projectTitle, _item }) => (
								`${projectTitle}, ${dictionary.accessibilityLabelProposalNumber || "Proposal #"}${_item.proposalNumber}`
							)}
							noResultsLabel={dictionary.noResults || "No Saved Proposals"}
							captionText="Proposals"
						/>
					</div>
				</BasePadding>
			</VerticalSpacing>
		</div>
	);
};

ProposalListView.propTypes = {
	proposalListId: PropTypes.string
};

export default ProposalListView;
